import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useCanvasEnv } from "../lib/envContext";
import { ConfirmDialog } from "../components/ConfirmDialog";

const HEADER = '"course_id","user_id","role","section_id","status"';
const PLACEHOLDER = `${HEADER}
"SU26 ACCT    1A 18W1","1000150996","Student","SU26 ACCT    1A 50906","Inactive"`;

/**
 * Test-upload entry point.
 *
 * Submits a fix CSV to Canvas's SIS Imports endpoint and **redirects to
 * /uploads/{uploadId}** so the user lands on the persistent Upload Detail
 * page. That page polls Canvas, renders the lifecycle, and gives access
 * to download/resubmit — making it the single place to track a Canvas
 * submission, regardless of whether it was initiated here or via the
 * Uploads list.
 *
 * Pre-row failures (client-side validation, server-side validation, network
 * before the row was created) stay on this page with an inline error. If
 * the row was created and Canvas just rejected the POST, the upload exists
 * in `ERROR` status and we still redirect — the detail page renders the
 * failure clearly in its lifecycle stepper.
 */
export function TestUploadPage() {
  const { env } = useCanvasEnv();
  const nav = useNavigate();
  const [csv, setCsv] = useState(HEADER + "\n");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProdConfirm, setShowProdConfirm] = useState(false);

  const { dataRows, valid, validationError, headerOk } = useMemo(
    () => validate(csv),
    [csv],
  );

  async function reallySubmit() {
    setError(null);
    setShowProdConfirm(false);
    setSubmitting(true);
    try {
      const { response } = await api.submitNewUpload({
        csv,
        trigger: "manual",
      });
      // Whether Canvas accepted or rejected the POST, the row exists.
      // The Upload Detail page renders both cases (live polling for
      // in-flight; final-result panel + error stepper for ERROR).
      nav(`/uploads/${response.uploadId}`);
    } catch (e) {
      // Pre-row failures (validation, network, unparseable response) —
      // keep the user here with their CSV intact so they can edit + retry.
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  function handleSubmit() {
    if (env === "prod") {
      setShowProdConfirm(true);
    } else {
      reallySubmit();
    }
  }

  return (
    <section>
      <div className="page-head">
        <h1>Test submission</h1>
        <div className="muted small">
          Send a fix CSV directly to Canvas. Every submission appears in{" "}
          <a href="/uploads" className="muted-link">
            Canvas submissions
          </a>
          .
        </div>
      </div>

      {env === "prod" ? (
        <div className="warn-banner warn-banner-prod">
          <strong>Production Canvas.</strong>
          <span>
            {" "}
            This will change real Canvas data and can't be undone from this
            dashboard. Double-check the rows below.
          </span>
        </div>
      ) : (
        <div className="warn-banner">
          <strong>Live submission to Canvas (test).</strong>
          <span>
            {" "}
            This sends the CSV straight to Canvas (test). The test environment
            refreshes monthly, so keep submissions small.
          </span>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2>Fix CSV</h2>
          <div className={"muted small " + (valid ? "" : "warn-text")}>
            {dataRows} data row{dataRows === 1 ? "" : "s"}
            {!headerOk && " · header row is wrong"}
          </div>
        </div>

        <textarea
          className="csv-textarea"
          value={csv}
          placeholder={PLACEHOLDER}
          spellCheck={false}
          disabled={submitting}
          onChange={(e) => setCsv(e.target.value)}
          rows={10}
        />

        {validationError && (
          <div className="error inline">{validationError}</div>
        )}

        <div className="actions" style={{ marginTop: 14 }}>
          <button
            className={
              "btn btn-large " + (env === "prod" ? "btn-danger" : "btn-primary")
            }
            disabled={!valid || submitting}
            onClick={handleSubmit}
          >
            {submitting
              ? "Sending…"
              : env === "prod"
                ? "Send to Canvas (production)"
                : "Send to Canvas (test)"}
          </button>
          <button
            className="btn"
            disabled={submitting}
            onClick={() => {
              setCsv(HEADER + "\n");
              setError(null);
            }}
          >
            Reset
          </button>
        </div>
        {error && <div className="error">{error}</div>}
        {submitting && (
          <div className="muted small" style={{ marginTop: 8 }}>
            Sending to Canvas… you'll be taken to the upload page as soon as
            it's submitted.
          </div>
        )}
      </div>

      {showProdConfirm && (
        <ConfirmDialog
          title="Confirm production submission"
          message={`You're about to send ${dataRows} row(s) directly to production Canvas. This will deactivate the listed enrollments for real students and can't be undone.`}
          confirmPhrase="SUBMIT PROD"
          confirmLabel="Send to production"
          danger
          onConfirm={reallySubmit}
          onCancel={() => setShowProdConfirm(false)}
        />
      )}
    </section>
  );
}

function validate(csv: string): {
  dataRows: number;
  valid: boolean;
  headerOk: boolean;
  validationError: string | null;
} {
  const lines = csv
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) {
    return {
      dataRows: 0,
      valid: false,
      headerOk: false,
      validationError: null,
    };
  }

  const headerOk = lines[0] === HEADER;
  if (!headerOk) {
    return {
      dataRows: 0,
      valid: false,
      headerOk: false,
      validationError: `Header must be exactly: ${HEADER}`,
    };
  }

  const dataRows = lines.slice(1);
  if (dataRows.length === 0) {
    return { dataRows: 0, valid: false, headerOk: true, validationError: null };
  }
  for (let i = 0; i < dataRows.length; i++) {
    const fields = (dataRows[i] as string).match(/"([^"]*)"/g);
    if (!fields || fields.length !== 5) {
      return {
        dataRows: dataRows.length,
        valid: false,
        headerOk: true,
        validationError: `Row ${i + 1}: expected 5 quoted fields. Got: ${(dataRows[i] as string).substring(0, 60)}`,
      };
    }
  }
  return {
    dataRows: dataRows.length,
    valid: true,
    headerOk: true,
    validationError: null,
  };
}
